import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ServerTranslateService} from 'src/app/services/server-translate.service';
import {Subject, Subscription, Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {
  OpenStreetMapController,
  MapEventsController,
  MapLayersController,
  MapDrawingController
} from './openstreetmap.controller';
import {
  MapInitOptions,
  MapControllerInput,
  MapClickEvent,
  MapMoveEvent,
  MapMouseMoveEvent,
  MarkerClickEvent
} from './interfaces';
import {MsfSelect} from '@components/select/select';
import {WeatherStationManager} from './managers/weather-statition.manager';
import {IndexManager} from './managers/index.manager';
import {DatePickerComponent} from '@components/date-picker/date-picker.component';
import {FormsModule} from '@angular/forms';
import {GoToCoordinatesModalComponent} from '../go-to-coordinates-modal/go-to-coordinates-modal.component';
import {LegendItem} from './legend-configs';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {GeoFileLoaderManager, GeoFileLayer} from './managers/geo-file-loader.manager';
import {FileUploadComponent} from '@components/file-upload/file-upload.component';
import {Dialog} from 'primeng/dialog';

import {SatelliteStyleSelectorComponent, SatelliteStyleMenuItem} from '@components/satellite-style-selector/satellite-style-selector.component';
import {
  BaseLayerType,
  IBaseLayerConfig
} from '@components/openstreetmap/controllers/map-layers.controller';
import {ButtonDirective} from 'primeng/button';
import {ButtonComponent} from '@components/button/button.component';
import {INDEX_LAYER_OPTIONS, IndexLayerOption} from '@shared/data/index-layer-options-data';
import {VisibilityManager} from './managers/visibility.manager';
import {OsmMapControllerInputManager} from './managers/osm-map-controller-input.manager';
import {
  initializeOpenStreetMap,
  loadLeafletFromCDNForOsm,
  tryInitializeOpenStreetMapWhenReady,
  OsmMapInitHost,
} from './managers/osm-map-initialization.manager';
import {OsmIndexLegendGeoOverlayManager, OsmIndexLegendGeoOverlayHost} from './managers/osm-index-legend-geo-overlay.manager';
import {OsmDrawingToolbarComponent} from './components/osm-drawing-toolbar/osm-drawing-toolbar.component';
import {createDefaultOsmMapOptions, resolveOsmMapLayers} from './constants/osm-map-default-config';
import {
  OsmMapBaselayerHost,
  OsmOverlaySourceType,
  resetOsmSatelliteUi,
  switchOsmBaseLayer,
  switchOsmOverlayLayer,
} from './constants/osm-map-baselayer.util';
import {
  clearAllOsmDrawnShapes,
  enableOsmBoxPolygonDrawing,
  enableOsmCustomPolygonDrawing,
  enableOsmPolygonDrawing,
  enableOsmRectangleDrawing,
  isOsmDistanceMeasurementMode,
  isOsmDrawingBoxPolygonActive,
  isOsmDrawingCustomPolygonActive,
  isOsmDrawingPolygonActive,
  isOsmDrawingRectangleActive,
  OsmMapDrawingHost,
  toggleOsmDistanceMeasurement,
} from './constants/osm-map-drawing-operations.util';
import {setupOsmMapKeyboardHandlers, updateOsmRegionLabelsOnZoom, OsmMapRegionLabelsHost} from './constants/osm-map-keyboard-region.util';
import {SatelliteTimelineComponent, SatelliteTimelineDate} from './components/satellite-timeline/satellite-timeline.component';
import {SatelliteTimelineManager, SatelliteTimelineHost, SatelliteDisplayMode} from './managers/satellite-timeline.manager';
import {SatelliteProductsService} from '@services/satellite-products.service';
import {ElectronicFieldPolygonService} from '@app/pages/satellite-images/services/electronic-field-polygon.service';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

@Component({
  selector: 'app-openstreetmap',
  standalone: true,
  imports: [CommonModule, MsfSelect, TranslatePipe, DatePickerComponent, FormsModule, GoToCoordinatesModalComponent, FileUploadComponent, Dialog, SatelliteStyleSelectorComponent, ButtonDirective, ButtonComponent, OsmDrawingToolbarComponent, SatelliteTimelineComponent],
  templateUrl: './openstreetmap.component.html',
  styleUrls: ['./openstreetmap.component.scss'],
})
export class OpenStreetMapComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  selectedIndexLayer: string | null = null;
  selectedIndexDate: Date | null = null;
  indexAvailableDates: string[] | null = [];
  currentYear = new Date().getFullYear();
  currentBaseLayer: 'plan' | 'google' = 'plan';
  currentSatelliteLayer: OsmOverlaySourceType | null = null;
  satelliteDisplayMode: SatelliteDisplayMode | null = null;
  pendingAutoSelectLatest = false;
  timelineInitialSelectionDone = false;
  currentSatelliteLabel: string = 'Satellite';
  isLoading: boolean = false;
  @Input() showIndexPanel: boolean = false;
  @Output() legendCloseClicked = new EventEmitter<void>();
  @Output() indexLayerApplied = new EventEmitter<void>();

  satelliteMenuItems: SatelliteStyleMenuItem[] = [];
  baseLayerMenuItems: SatelliteStyleMenuItem[] = [
    {label: 'PLAN', value: 'plan'},
    {label: 'Google', value: 'google'},
  ];
  goToCoordinatesModalVisible = false;
  baseSelectorVisible = false;
  satelliteSelectorVisible = false;
  // Weather/Overlay layer properties
  selectedWeatherLayer: string | null = null;
  weatherLayerOpacity: number = 60;
  indexLayerOpacity: number = 60;
  satelliteLayerOpacity: number = 100;
  indexLayerVisible: boolean = true;

  // Geo file upload properties
  geoFileUploadModalVisible: boolean = false;
  geoFileUploadType: 'gpkg' | 'geojson' | 'kml' | null = null;
  geoFileUploadAccept: string = '';
  isGeoFileLoading: boolean = false;
  loadedGeoFileLayers: GeoFileLayer[] = [];

  // Cursor coordinates tracking
  currentCursorLat: number | null = null;
  currentCursorLng: number | null = null;
  currentZoom: number = 0;
  currentLegend: LegendItem[] | null = null;
  currentLegendTitle: string | null = null;
  legendCollapsed = false;
  showIndexModal = false;

  // ---- New timeline / calendar design state ----
  calendarDateRange: Date[] | null = null;
  indexMenuItems: { label: string; value: string }[] = [];

  timelineDates: SatelliteTimelineDate[] = [];
  timelineRangeStart: string | null = null;
  timelineRangeEnd: string | null = null;
  selectedTimelineDates: string[] = [];
  timelineLoading = false;
  timelineLoadedYears = new Set<number>();
  timelineLoadingYears = new Set<number>();
  timelineCustomLayerIds = new Set<string>();
  timelinePolygonIds = new Set<string>();


  @ViewChild('mapContainer', {static: true}) mapContainer!: ElementRef;
  @ViewChild('timelineCalendar') timelineCalendar?: DatePickerComponent;

  @Input() mapController$ = new Subject<MapControllerInput>();

  @Input() mapOptions: MapInitOptions = createDefaultOsmMapOptions();

  @Output() mapReady = new EventEmitter<any>();

  // Output event for drawing completion
  @Output() drawingCompleted = new EventEmitter<{
    success: boolean;
    error?: any;
    shape?: {
      type: 'polygon' | 'rectangle' | 'box-polygon' | 'custom-polygon';
      coordinates: [number, number][];
      center: { lat: number; lng: number };
      layer: any;
    };
  }>();

  mapLayers: IBaseLayerConfig = resolveOsmMapLayers();

  // =========================================================================
  // COMPUTED PROPERTIES (derived from mapOptions)
  // =========================================================================

  /**
   * Get map container height
   */
  get height(): string {
    return this.mapOptions.height || '400px';
  }

  /**
   * Get drawing mode status
   */
  get drawingMode(): boolean {
    return this.mapOptions.drawingMode || false;
  }

  // =========================================================================
  // OUTPUTS (as RxJS Observables through the controller)
  // =========================================================================

  // Bridge subjects for observables to ensure proper emission
  private mapRightClickSubject$ = new Subject<MapClickEvent>();
  mapClickSubject$ = new Subject<MapClickEvent>();

  // Expose controller observables out
  mapClick$!: Observable<MapClickEvent>;
  mapDblClick$!: Observable<MapClickEvent>;
  mapRightClick$: Observable<MapClickEvent> = this.mapRightClickSubject$.asObservable();
  markerClick$!: Observable<MarkerClickEvent>;
  polygonClick$!: Observable<MapClickEvent>;
  coordinatesChange$!: Observable<[number, number]>;
  mapMove$!: Observable<MapMoveEvent>;
  mapDragEnd$!: Observable<MapMoveEvent>;
  mapZoomChange$!: Observable<number>;
  mapMouseMove$!: Observable<MapMouseMoveEvent>;
  shapesChanged$!: Observable<any>;
  monitoringPointsClick$!: Observable<any>;

  // =========================================================================
  // INTERNAL STATE
  // =========================================================================

  private translate = inject(TranslateService);
  private serverTranslate = inject(ServerTranslateService);
  public weatherStationManager = inject(WeatherStationManager);
  public regionManager = inject(RegionManagerService);
  public indexManager = inject(IndexManager);
  public geoFileLoaderManager = inject(GeoFileLoaderManager);
  public satelliteProducts = inject(SatelliteProductsService);
  public polygonService = inject(ElectronicFieldPolygonService);
  readonly visibilityManager = new VisibilityManager();
  public readonly satelliteTimeline = new SatelliteTimelineManager(
    () => this as unknown as SatelliteTimelineHost & { onTimelineSelectionChanged: () => void },
  );
  private readonly controllerInput = new OsmMapControllerInputManager(() => ({
    isMapInitialized: this.isMapInitialized,
    mapInstance: this.mapInstance,
    layersController: this.layersController,
    drawingController: this.drawingController,
    eventsController: this.eventsController,
    mapOptions: this.mapOptions,
    visibilityManager: this.visibilityManager,
  }));
  public readonly osmIndexOverlay = new OsmIndexLegendGeoOverlayManager(
    () => this as unknown as OsmIndexLegendGeoOverlayHost,
  );
  private osmController!: OpenStreetMapController;
  private eventsController!: MapEventsController;
  private layersController!: MapLayersController;
  private drawingController!: MapDrawingController;

  private destroy$ = new Subject<void>();
  private controllerSubscription!: Subscription;

  isMapInitialized = false;
  public mapInstance: any;
  private deferIndexApply = false;

  // Index layer options
  readonly indexLayerOptions: IndexLayerOption[] = INDEX_LAYER_OPTIONS;

  // =========================================================================
  // LIFECYCLE HOOKS
  // =========================================================================

  ngOnInit(): void {
    this.setupKeyboardHandlers();
    this.buildIndexMenuItems();
    this.buildSatelliteMenuItems();
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateLocalization();
        this.buildIndexMenuItems();
        this.buildSatelliteMenuItems();
      });

    this.subscribeToControllerInput();
    if (this.mapOptions?.indexOpacityToggle === undefined)
    {
      this.mapOptions.indexOpacityToggle = true;
    }
  }

  ngAfterViewInit(): void {
    if (typeof (window as any).L !== 'undefined') {
      setTimeout(() => {
        tryInitializeOpenStreetMapWhenReady(this as unknown as OsmMapInitHost);
      }, 100);
    } else {
      loadLeafletFromCDNForOsm(this as unknown as OsmMapInitHost);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mapOptions'] && this.mapInstance && !changes['mapOptions'].firstChange) {
      this.applyMapOptions();
    }
  }

  /**
   * Backward-compatible layer flag for parent pages (opacity controls, etc.).
   * Returns `satellite` when a Sentinel/Landsat/UAV overlay or index is active.
   */
  get currentMapLayer(): string {
    if (this.currentSatelliteLayer || this.selectedIndexLayer) {
      return 'satellite';
    }
    return this.currentBaseLayer;
  }

  switchBaseMapLayer(layerType: 'plan' | 'google'): void {
    switchOsmBaseLayer(this as unknown as OsmMapBaselayerHost, layerType);
  }

  toggleBaseSelector(): void {
    this.baseSelectorVisible = !this.baseSelectorVisible;
    if (this.baseSelectorVisible) {
      this.satelliteSelectorVisible = false;
    }
  }

  toggleSatelliteSelector(): void {
    this.satelliteSelectorVisible = !this.satelliteSelectorVisible;
    if (this.satelliteSelectorVisible) {
      this.baseSelectorVisible = false;
    }
  }

  get hasActiveSatelliteLayer(): boolean {
    return !!this.currentSatelliteLayer || !!this.selectedIndexLayer;
  }

  clearActiveSatelliteLayer(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.satelliteSelectorVisible = false;
    this.onSatelliteMenuSelect(null);
  }

  onBaseLayerSelect(value: string | null): void {
    this.baseSelectorVisible = false;
    if (value === 'plan' || value === 'google') {
      this.switchBaseMapLayer(value);
    }
  }

  get displayBaseLayerLabel(): string {
    return this.currentBaseLayer === 'google' ? 'Google' : 'PLAN';
  }

  switchMapLayer(layerType: BaseLayerType): void {
    if (layerType === 'plan' || layerType === 'google') {
      switchOsmBaseLayer(this as unknown as OsmMapBaselayerHost, layerType);
    }
  }

  switchSatelliteOverlay(overlayType: OsmOverlaySourceType, displayMode: SatelliteDisplayMode = 'range'): void {
    this.satelliteDisplayMode = displayMode;
    switchOsmOverlayLayer(this as unknown as OsmMapBaselayerHost, overlayType);
    if (!this.selectedIndexLayer) {
      this.satelliteTimeline.reloadDates();
      this.calendarDateRange = null;
    }
  }

  clearSatelliteOverlay(): void {
    this.satelliteTimeline.clearCustomLayers();
    switchOsmOverlayLayer(this as unknown as OsmMapBaselayerHost, null);
    this.satelliteDisplayMode = null;
    if (!this.selectedIndexLayer) {
      this.satelliteTimeline.reloadDates();
      this.calendarDateRange = null;
    }
  }

  onTimelineSelectionChanged(): void {
    this.syncCalendarFromTimelineRange();
  }

  // =========================================================================
  // NEW DESIGN: SATELLITE MENU + CALENDAR + TIMELINE
  // =========================================================================

  private buildIndexMenuItems(): void {
    const lang = this.translate.currentLang || 'en-US';
    this.indexMenuItems = this.indexLayerOptions
      .filter((option) => !!option.value)
      .map((option) => ({
        label: option.names?.[lang] || option.value || '',
        value: option.value as string,
      }));
  }

  private buildSatelliteMenuItems(): void {
    const t = (key: string) => this.translate.instant(key);

    this.satelliteMenuItems = [
      {
        label: 'Sentinel',
        children: [
          {
            label: t('Images'),
            value: 'sentinel:range',
          },
          {
            label: 'INDEX_TILE_MAP.INDEXES',
            children: this.indexMenuItems.map((item) => ({
              label: item.label,
              value: `index:${item.value}`,
            })),
          },
        ],
      },
      {
        label: 'Landsat',
        children: [
          {
            label: t('Images'),
            value: 'landsat:range',
          },
        ],
      },
      {
        label: 'UAV',
        value: 'uav',
      },
    ];
  }

  onSatelliteMenuSelect(value: string | null): void {
    this.satelliteSelectorVisible = false;
    this.baseSelectorVisible = false;
    if (!value) {
      if (this.selectedIndexLayer) {
        this.osmIndexOverlay.clearIndexSelection();
        this.satelliteDisplayMode = null;
        this.satelliteTimeline.reloadDates();
        this.calendarDateRange = null;
      } else {
        this.clearSatelliteOverlay();
      }
      return;
    }

    if (value.startsWith('index:')) {
      const indexValue = value.slice('index:'.length);
      this.satelliteTimeline.clearCustomLayers();
      switchOsmOverlayLayer(this as unknown as OsmMapBaselayerHost, null);
      this.satelliteDisplayMode = null;
      this.selectedIndexLayer = indexValue;
      this.selectedIndexDate = null;
      this.satelliteTimeline.reloadDates();
      this.calendarDateRange = null;
      return;
    }

    const rangeMatch = /^(sentinel|landsat):(range|all)$/.exec(value);
    if (rangeMatch) {
      const overlay = rangeMatch[1] as OsmOverlaySourceType;
      const mode = rangeMatch[2] as SatelliteDisplayMode;
      if (this.selectedIndexLayer) {
        this.osmIndexOverlay.clearIndexSelection();
      }
      this.selectedIndexLayer = null;
      this.switchSatelliteOverlay(overlay, mode);
      return;
    }

    if (this.selectedIndexLayer) {
      this.osmIndexOverlay.clearIndexSelection();
    }
    this.satelliteDisplayMode = null;
    if (value === 'sentinel' || value === 'landsat') {
      this.switchSatelliteOverlay(value, 'range');
      return;
    }
    if (value === 'uav') {
      switchOsmOverlayLayer(this as unknown as OsmMapBaselayerHost, 'uav');
      this.satelliteTimeline.reloadDates();
      this.calendarDateRange = null;
    }
  }

  openTimelineCalendar(): void {
    this.baseSelectorVisible = false;
    this.satelliteSelectorVisible = false;
    this.timelineCalendar?.open();
  }

  get displaySatelliteLabel(): string {
    if (this.selectedIndexLayer) {
      const match = this.indexMenuItems.find((item) => item.value === this.selectedIndexLayer);
      return match?.label || this.selectedIndexLayer.toUpperCase();
    }
    if (this.currentSatelliteLayer === 'uav') {
      return 'UAV';
    }
    if (this.currentSatelliteLayer === 'sentinel') {
      return 'Sentinel';
    }
    if (this.currentSatelliteLayer === 'landsat') {
      return 'Landsat';
    }
    return this.currentSatelliteLabel;
  }

  get showTimelineControls(): boolean {
    return this.satelliteTimeline.isActive();
  }

  get showTimelineCalendar(): boolean {
    return this.showTimelineControls && (this.selectedIndexLayer != null || this.satelliteDisplayMode !== 'all');
  }

  get showTimelineAllButton(): boolean {
    return !this.selectedIndexLayer
      && (this.currentSatelliteLayer === 'sentinel' || this.currentSatelliteLayer === 'landsat');
  }

  get satelliteMenuSelection(): string | null {
    if (this.selectedIndexLayer) {
      return `index:${this.selectedIndexLayer}`;
    }
    if (
      this.currentSatelliteLayer
      && (this.currentSatelliteLayer === 'sentinel' || this.currentSatelliteLayer === 'landsat')
      && this.satelliteDisplayMode
    ) {
      return `${this.currentSatelliteLayer}:range`;
    }
    return this.currentSatelliteLayer;
  }

  /** Legend "X": remove every index layer loaded on the map (primary + extra timeline tiles). */
  onIndexLegendClose(): void {
    this.osmIndexOverlay.onLegendCloseClick();
    this.satelliteTimeline.reloadDates();
    this.calendarDateRange = null;
  }

  onCalendarRangeChange(value: Date | Date[] | null): void {
    if (this.satelliteDisplayMode === 'all') {
      return;
    }
    if (!value) {
      this.calendarDateRange = null;
      this.satelliteTimeline.clearSelection();
      return;
    }

    if (Array.isArray(value)) {
      if (value.length < 2 || !value[0] || !value[1]) {
        this.calendarDateRange = value;
        return;
      }
      const startIso = this.toIsoDate(value[0]);
      const endIso = this.toIsoDate(value[1]);
      this.calendarDateRange = [value[0], value[1]];
      this.satelliteTimeline.setRange(startIso, endIso);
      return;
    }

    const iso = this.toIsoDate(value);
    if (!this.timelineDates.some((entry) => entry.date === iso)) {
      return;
    }
    this.calendarDateRange = [value, value];
    this.satelliteTimeline.setRange(iso, iso);
  }

  onCalendarYearChange(year: number): void {
    if (year && !this.timelineLoadedYears.has(year)) {
      // Lazy-load the picked year so its dates become selectable on the timeline.
      this.satelliteTimeline.ensureYearLoaded(year);
    }
  }

  onTimelineDateSelect(date: string): void {
    if (this.satelliteDisplayMode === 'all' && !this.selectedIndexLayer) {
      this.satelliteDisplayMode = 'range';
      switchOsmOverlayLayer(this as unknown as OsmMapBaselayerHost, this.currentSatelliteLayer);
    }
    this.satelliteTimeline.selectTimelineDate(date);
    this.syncCalendarFromTimelineRange();
  }

  onTimelineAllSelect(): void {
    if (!this.showTimelineAllButton || this.satelliteDisplayMode === 'all') {
      return;
    }
    this.satelliteTimeline.activateAllMode();
    switchOsmOverlayLayer(this as unknown as OsmMapBaselayerHost, this.currentSatelliteLayer);
  }

  private syncCalendarFromTimelineRange(): void {
    if (!this.timelineRangeStart) {
      this.calendarDateRange = null;
      return;
    }
    const start = this.parseIsoDate(this.timelineRangeStart);
    const end = this.parseIsoDate(this.timelineRangeEnd ?? this.timelineRangeStart);
    if (!start || !end) {
      return;
    }
    this.calendarDateRange = [start, end];
  }

  private parseIsoDate(iso: string): Date | null {
    if (!iso) {
      return null;
    }
    const parts = iso.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) {
      return null;
    }
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  onTimelineReachStart(): void {
    this.satelliteTimeline.loadPreviousYear();
  }

  onTimelineReachEnd(): void {}

  get timelineSelectableDates(): string[] {
    return this.timelineDates.map((entry) => entry.date);
  }

  get translateCurrentLang(): string {
    return this.translate.currentLang || 'en-US';
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }



  // =========================================================================
  // CONTROLLER SUBSCRIPTION
  // =========================================================================

  private subscribeToControllerInput(): void {
    this.controllerSubscription = this.mapController$
      .pipe(takeUntil(this.destroy$))
      .subscribe((input: MapControllerInput) => {
        try {
          this.processControllerInput(input);
        } catch (err) {
          console.error('[OpenStreetMap] mapController subscription handler', err);
        }
      });
  }

  private processControllerInput(input: MapControllerInput): void {
    try {
      if (input.initialize) {
        this.applyMapOptions();
      }
      this.controllerInput.process(input);
    } catch (err) {
      console.error('[OpenStreetMap] processControllerInput', err);
    }
  }

  // =========================================================================
  // MAP INITIALIZATION
  // =========================================================================

  private applyMapOptions(): void {
    if (!this.mapInstance) {
      return;
    }

    // Apply height if specified
    if (this.mapOptions.height) {
      this.mapContainer.nativeElement.style.height = this.mapOptions.height;
      this.mapInstance.invalidateSize();
    }

    // Apply center and zoom if specified
    if (this.mapOptions.center && this.mapOptions.zoom !== undefined) {
      this.mapInstance.setView(this.mapOptions.center, this.mapOptions.zoom);
    }

    if (this.mapOptions.zoom) {
      this.mapInstance.setView(this.mapInstance.getCenter(), this.mapOptions.zoom);
    }
  }

  // =========================================================================
  // LAYER OPERATIONS
  // =========================================================================

  // =========================================================================
  // KEYBOARD HANDLING
  // =========================================================================

  private setupKeyboardHandlers(): void {
    const host = this;
    setupOsmMapKeyboardHandlers({
      get drawingController() {
        return host.drawingController;
      },
    });
  }

  initSatelliteLayer(): void {
    resetOsmSatelliteUi(this as unknown as OsmMapBaselayerHost);
  }

  // =========================================================================
  // LOCALIZATION
  // =========================================================================

  private updateLocalization(): void {
    // Update region labels with new language
    if (this.layersController) {
      this.layersController.updateRegionLabelsLanguage();
    }
  }

  // =========================================================================
  // PUBLIC API METHODS
  // =========================================================================

  /**
   * Get the map instance for direct Leaflet API access if needed
   */
  public getMapInstance(): any {
    return this.mapInstance;
  }

  /**
   * Get the events controller for manual event subscriptions if needed
   */
  public getEventsController(): MapEventsController {
    return this.eventsController;
  }

  /**
   * Get the layers controller for advanced layer operations
   */
  public getLayersController(): MapLayersController {
    return this.layersController;
  }

  setTimelineIndexLayersOpacity(opacityPercent: number, visible: boolean): void {
    this.satelliteTimeline.setIndexLayersOpacity(opacityPercent, visible);
  }

  setSatelliteLayerOpacityValue(opacityPercent: number): void {
    const pct = Math.max(0, Math.min(100, opacityPercent));
    this.satelliteLayerOpacity = pct;
    if (this.satelliteDisplayMode === 'all') {
      this.getLayersController()?.setOpacity('overlay', pct / 100);
    } else {
      this.satelliteTimeline.setSatelliteLayersOpacity(pct);
    }
  }

  /**
   * Get the drawing controller for advanced drawing operations
   */
  public getDrawingController(): MapDrawingController {
    return this.drawingController;
  }


  /**
   * Add a native Leaflet control (top-right) which emits monitoringPointsClick via eventsController
   */
  private addMonitoringPointsControl(): void {
    if (!this.mapInstance || !(window as any).L) return;
  }

  // =========================================================================
  // DRAWING CONTROLS
  // =========================================================================

  enablePolygonDrawing(): void {
    enableOsmPolygonDrawing(this as unknown as OsmMapDrawingHost);
  }

  enableRectangleDrawing(): void {
    enableOsmRectangleDrawing(this as unknown as OsmMapDrawingHost);
  }

  isDrawingRectangle(): boolean {
    return isOsmDrawingRectangleActive(this as unknown as OsmMapDrawingHost);
  }

  isDrawingPolygon(): boolean {
    return isOsmDrawingPolygonActive(this as unknown as OsmMapDrawingHost);
  }

  toggleDistanceMeasurement(): void {
    toggleOsmDistanceMeasurement(this as unknown as OsmMapDrawingHost);
  }

  isDistanceMeasurementMode(): boolean {
    return isOsmDistanceMeasurementMode(this as unknown as OsmMapDrawingHost);
  }

  enableBoxPolygonDrawing(): void {
    enableOsmBoxPolygonDrawing(this as unknown as OsmMapDrawingHost);
  }

  enableCustomPolygonDrawing(): void {
    enableOsmCustomPolygonDrawing(this as unknown as OsmMapDrawingHost);
  }

  isDrawingBoxPolygon(): boolean {
    return isOsmDrawingBoxPolygonActive(this as unknown as OsmMapDrawingHost);
  }

  isDrawingCustomPolygon(): boolean {
    return isOsmDrawingCustomPolygonActive(this as unknown as OsmMapDrawingHost);
  }

  clearAllDrawnShapes(): void {
    clearAllOsmDrawnShapes(this as unknown as OsmMapDrawingHost);
  }

  // =========================================================================
  // GO TO COORDINATES MODAL
  // =========================================================================


  openGoToCoordinatesModal() {
    this.goToCoordinatesModalVisible = true;
  }

  /**
   * Update region label font sizes based on current zoom level
   * Called whenever the map zoom changes
   */
  private updateRegionLabelsOnZoom(): void {
    updateOsmRegionLabelsOnZoom(this as unknown as OsmMapRegionLabelsHost);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.controllerSubscription) {
      this.controllerSubscription.unsubscribe();
    }

    if (this.mapInstance) {
      this.mapInstance.remove();
    }

    if (this.geoFileLoaderManager) {
      this.geoFileLoaderManager.destroy();
    }
  }
}
